import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock3,
  Filter,
  Mail,
  Phone,
  RefreshCw,
  UserRoundCheck,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import { api, asItems, asObject, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  cleanNoteText,
  count,
  EntityLink,
  FilterActions,
  FilterField,
  formatDateTime,
  labelize,
  leadLink,
  MobileBack,
  officeLink,
  options,
  PropertyThumb,
  propertyLink,
  SearchField,
  StatusPill,
  SummaryCards,
  toDateTimeLocalValue,
  userLink,
  WorkbenchEmpty,
  WorkbenchError,
  WorkbenchHeader,
  WorkbenchLoading,
  WorkbenchPagination,
} from "@/components/admin/AdminWorkbenchUi";

const PROPERTY_PAGE_SIZE = 12;
const VISIT_PAGE_SIZE = 50;
const FILTER_KEYS = [
  "q", "status", "agent_id", "office_id", "property_id", "buyer_id",
  "assignment", "timing", "visit_from", "visit_to", "sort",
];
const ADVANCED_FILTERS = ["buyer_id", "assignment", "timing", "visit_from", "visit_to", "sort"];
const EMPTY_FILTERS = {
  q: "", status: "", agent_id: "", office_id: "", property_id: "", buyer_id: "",
  assignment: "", timing: "", visit_from: "", visit_to: "", sort: "schedule",
};

const readFilters = params => Object.fromEntries(
  FILTER_KEYS.map(key => [key, params.get(key) || EMPTY_FILTERS[key]]),
);

const requestParams = (params, extras = {}) => {
  const values = {};
  FILTER_KEYS.forEach(key => {
    const value = params.get(key);
    if (value) values[key] = value;
  });
  return { ...values, ...extras };
};

// Admin scheduling intentionally does not expose the protected start/completion
// verification codes. Those actions remain in the established buyer/agent flow.
export default function AdminSiteVisitWorkbench() {
  const [params, setParams] = useSearchParams();
  const queryString = params.toString();
  const selectedVisitId = params.get("select") || "";
  const expandedPropertyId = params.get("property") || "";
  const requestedPage = Number(params.get("page") || 1);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? Math.min(requestedPage, 1_000_000) : 1;

  const [draft, setDraft] = useState(() => readFilters(params));
  const [showMore, setShowMore] = useState(() => ADVANCED_FILTERS.some(key => params.get(key)));
  const [data, setData] = useState({ items: [], summary: {}, total: 0, page: 1, limit: PROPERTY_PAGE_SIZE });
  const [filters, setFilters] = useState({});
  const [visits, setVisits] = useState({ items: [], total: 0, page: 1, limit: VISIT_PAGE_SIZE });
  const [visitPage, setVisitPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [visitsError, setVisitsError] = useState("");
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [assignment, setAssignment] = useState({ agent_id: "", office_id: "", visit_at: "", note: "" });
  const [statusNote, setStatusNote] = useState("");

  // Entity selection/expansion lives in the URL, but must not cause the parent
  // server query to reload. This key contains filter and pagination state only.
  const appliedQuery = useMemo(() => {
    const currentParams = new URLSearchParams(queryString);
    const applied = new URLSearchParams();
    FILTER_KEYS.forEach(key => currentParams.get(key) && applied.set(key, currentParams.get(key)));
    if (page > 1) applied.set("page", String(page));
    return applied.toString();
  }, [queryString, page]);

  const activeFilterCount = useMemo(() => {
    const applied = new URLSearchParams(appliedQuery);
    return FILTER_KEYS.filter(key => key !== "sort" && applied.get(key)).length;
  }, [appliedQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const applied = new URLSearchParams(appliedQuery);
      const response = await api.get("/v1/admin/site-visits-workbench/properties", {
        params: requestParams(applied, { page, limit: PROPERTY_PAGE_SIZE }),
      });
      const payload = asObject(response.data);
      setData({ ...payload, items: asItems(payload) });
    } catch (requestError) {
      setData({ items: [], summary: {}, total: 0, page: 1, limit: PROPERTY_PAGE_SIZE });
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, page, reloadKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let current = true;
    api.get("/v1/admin/site-visits-workbench/filters")
      .then(response => current && setFilters(asObject(response.data)))
      .catch((requestError) => {
        if (current) {
          setFilters({});
          toast.error(`Site Visit filters could not be loaded: ${formatApiError(requestError) || "Request failed."}`);
        }
      });
    return () => { current = false; };
  }, []);

  useEffect(() => {
    const applied = new URLSearchParams(queryString);
    setDraft(readFilters(applied));
    if (ADVANCED_FILTERS.some(key => applied.get(key))) setShowMore(true);
  }, [queryString]);

  useEffect(() => {
    if (!expandedPropertyId) {
      setVisits({ items: [], total: 0, page: 1, limit: VISIT_PAGE_SIZE });
      setVisitsError("");
      return undefined;
    }
    let current = true;
    setVisitsLoading(true);
    setVisitsError("");
    api.get(`/v1/admin/site-visits-workbench/properties/${expandedPropertyId}/visits`, {
      params: requestParams(new URLSearchParams(appliedQuery), { page: visitPage, limit: VISIT_PAGE_SIZE }),
    })
      .then(response => {
        if (!current) return;
        const payload = asObject(response.data);
        setVisits({ ...payload, items: asItems(payload) });
      })
      .catch(requestError => {
        if (!current) return;
        setVisits({ items: [], total: 0, page: 1, limit: VISIT_PAGE_SIZE });
        setVisitsError(formatApiError(requestError));
      })
      .finally(() => current && setVisitsLoading(false));
    return () => { current = false; };
  }, [expandedPropertyId, visitPage, appliedQuery, reloadKey]);

  const loadDetail = useCallback(async () => {
    if (!selectedVisitId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const response = await api.get(`/v1/admin/site-visits-workbench/${selectedVisitId}`);
      const payload = asObject(response.data);
      const visit = payload.visit || {};
      setDetail(payload);
      setAssignment({
        agent_id: visit.agent_id || "",
        office_id: visit.office_id || "",
        visit_at: toDateTimeLocalValue(visit.visit_at),
        note: "",
      });
    } catch (requestError) {
      toast.error(formatApiError(requestError));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedVisitId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const applyFilters = () => {
    const next = new URLSearchParams();
    FILTER_KEYS.forEach(key => {
      if (draft[key] && !(key === "sort" && draft[key] === "schedule")) next.set(key, draft[key]);
    });
    setVisitPage(1);
    setParams(next);
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setShowMore(false);
    setVisitPage(1);
    setParams(new URLSearchParams());
  };

  const toggleProperty = propertyId => {
    const next = new URLSearchParams(params);
    next.delete("select");
    if (expandedPropertyId === propertyId) next.delete("property");
    else next.set("property", propertyId);
    setVisitPage(1);
    setParams(next);
  };

  const selectVisit = visitId => {
    const next = new URLSearchParams(params);
    if (visitId) next.set("select", visitId);
    else next.delete("select");
    setParams(next);
  };

  const changePropertyPage = nextPage => {
    const next = new URLSearchParams(params);
    if (nextPage > 1) next.set("page", String(nextPage));
    else next.delete("page");
    next.delete("property");
    next.delete("select");
    setVisitPage(1);
    setParams(next);
  };

  const mutate = async (path, body, success) => {
    setSaving(true);
    try {
      await api.post(path, body);
      toast.success(success);
      setStatusNote("");
      setAssignment(current => ({ ...current, note: "" }));
      setReloadKey(value => value + 1);
      await loadDetail();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const summary = data.summary || {};
  const cards = useMemo(() => [
    { label: "Visits", value: summary.total_visits, icon: CalendarClock, tone: "bg-primary/10 text-primary" },
    { label: "Properties", value: summary.properties, icon: Building2, tone: "bg-sky-500/10 text-sky-600 dark:text-sky-300" },
    { label: "Buyers", value: summary.buyers, icon: UsersRound, tone: "bg-violet-500/10 text-violet-600 dark:text-violet-300" },
    { label: "Unassigned", value: summary.unassigned, icon: UserRoundCheck, tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
    { label: "Overdue", value: summary.overdue, icon: AlertTriangle, tone: "bg-destructive/10 text-destructive" },
    { label: "Completed", value: summary.completed, icon: CheckCircle2, tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  ], [summary]);

  return <div className="space-y-5" data-testid="admin-site-visit-workbench">
    <WorkbenchHeader
      eyebrow="Site Visit Management"
      title="Site Visit Workbench"
      description="Start with a property, expand every matching buyer visit, and inspect who requested, scheduled, assigned and handled it."
      actions={<><Button asChild variant="ghost"><Link to="/dashboard/admin/site-visits" title="Open Site Visits overview">Overview</Link></Button><Button asChild variant="outline"><Link to="/dashboard/admin/site-visits/assignments" title="Open Site Visit assignment and management"><UserRoundCheck className="h-4 w-4"/>Manage assignments</Link></Button><Button variant="outline" onClick={() => setReloadKey(value => value + 1)} disabled={loading} title="Refresh Site Visit data"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}/>Refresh</Button></>}
    />
    <SummaryCards items={cards} loading={loading}/>

    <form onSubmit={event => { event.preventDefault(); applyFilters(); }} className="rounded-2xl border bg-card p-4 shadow-sm" aria-label="Site Visit filters">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="flex items-center gap-2 font-display text-lg font-black"><Filter className="h-4 w-4 text-primary"/>Search & filters</h2><p className="mt-1 text-xs text-muted-foreground">Filters are server-backed and retained in the page URL.</p></div>
        {activeFilterCount > 0 && <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">{activeFilterCount} active {activeFilterCount === 1 ? "filter" : "filters"}</Badge>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <div className="sm:col-span-2 xl:col-span-1"><SearchField value={draft.q} onChange={value => setDraft(current => ({ ...current, q: value }))} placeholder="Buyer, phone, property or visit ID"/></div>
        <FilterField label="Status" value={draft.status} onChange={value => setDraft(current => ({ ...current, status: value }))} choices={options(filters.statuses, "All statuses")}/>
        <FilterField label="Agent" value={draft.agent_id} onChange={value => setDraft(current => ({ ...current, agent_id: value }))} choices={options(filters.agents, "All agents")}/>
        <FilterField label="Office" value={draft.office_id} onChange={value => setDraft(current => ({ ...current, office_id: value }))} choices={options(filters.offices, "All offices")}/>
        <FilterField label="Property" value={draft.property_id} onChange={value => setDraft(current => ({ ...current, property_id: value }))} choices={options(filters.properties, "All properties")}/>
      </div>
      <button type="button" onClick={() => setShowMore(value => !value)} aria-expanded={showMore} aria-controls="site-visit-advanced-filters" className="mt-3 inline-flex min-h-9 items-center gap-2 rounded-lg px-2 text-sm font-bold text-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" title={showMore ? "Hide additional Site Visit filters" : "Show additional Site Visit filters"}>
        <Filter className="h-4 w-4"/>More filters {showMore ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}
      </button>
      {showMore && <div id="site-visit-advanced-filters" className="mt-3 grid gap-3 border-t pt-4 sm:grid-cols-2 xl:grid-cols-6">
        <FilterField label="Buyer" value={draft.buyer_id} onChange={value => setDraft(current => ({ ...current, buyer_id: value }))} choices={options(filters.buyers, "All buyers")}/>
        <FilterField label="Assignment" value={draft.assignment} onChange={value => setDraft(current => ({ ...current, assignment: value }))} choices={options([{ value: "ASSIGNED", label: "Assigned" }, { value: "UNASSIGNED", label: "Unassigned" }], "Any assignment")}/>
        <FilterField label="Timing" value={draft.timing} onChange={value => setDraft(current => ({ ...current, timing: value }))} choices={options([{ value: "TODAY", label: "Today" }, { value: "UPCOMING", label: "Upcoming" }, { value: "OVERDUE", label: "Overdue" }], "Any timing")}/>
        <FilterField label="Visit from" type="date" max={draft.visit_to || undefined} value={draft.visit_from} onChange={value => setDraft(current => ({ ...current, visit_from: value }))}/>
        <FilterField label="Visit to" type="date" min={draft.visit_from || undefined} value={draft.visit_to} onChange={value => setDraft(current => ({ ...current, visit_to: value }))}/>
        <FilterField label="Sort" value={draft.sort} onChange={value => setDraft(current => ({ ...current, sort: value }))} choices={options([{ value: "schedule", label: "Schedule priority" }, { value: "latest", label: "Latest update" }, { value: "oldest", label: "Oldest" }, { value: "status", label: "Attention required" }], "Sort")}/>
      </div>}
      <div className="mt-4 flex justify-end border-t pt-4"><FilterActions onReset={resetFilters} loading={loading}/></div>
    </form>

    {error ? <WorkbenchError message={error} onRetry={() => setReloadKey(value => value + 1)}/> : <section className="overflow-hidden rounded-2xl border bg-card shadow-sm" aria-label="Property Site Visit results">
      <div className="grid min-h-[560px] xl:h-[72vh] xl:max-h-[780px] xl:grid-cols-[minmax(0,1.55fr)_minmax(360px,.75fr)]">
        <div className={`${selectedVisitId ? "hidden xl:block" : "block"} min-w-0 xl:overflow-y-auto`}>
          <div className="border-b p-4"><h2 className="font-display text-lg font-black">Properties with Site Visits</h2><p className="mt-1 text-xs text-muted-foreground">{loading ? "Loading property relationships…" : `${count(data.total)} properties found · expand a property to see every buyer visit`}</p></div>
          {loading ? <WorkbenchLoading/> : data.items.length === 0 ? <WorkbenchEmpty title="No Site Visits found" detail="Try a wider date range or clear one or more filters."/> : <div className="space-y-3 p-3 sm:p-4">{data.items.map(property => <PropertyVisitGroup
            key={property.property_id}
            property={property}
            expanded={expandedPropertyId === property.property_id}
            selectedVisitId={selectedVisitId}
            visits={visits.items}
            visitTotal={visits.total}
            visitPage={visitPage}
            visitLoading={visitsLoading}
            visitError={visitsError}
            onToggle={() => toggleProperty(property.property_id)}
            onSelect={selectVisit}
            onVisitPage={setVisitPage}
          />)}</div>}
          {!loading && (
            <WorkbenchPagination page={page} limit={PROPERTY_PAGE_SIZE} total={data.total} onPage={changePropertyPage}/>
          )}
        </div>
        <aside className={`${selectedVisitId ? "block" : "hidden xl:block"} min-w-0 bg-muted/15 xl:border-l xl:overflow-y-auto`} aria-label="Selected Site Visit details">
          {!selectedVisitId ? <div className="flex h-full items-center justify-center p-10 text-center text-sm text-muted-foreground">Expand a property and select a buyer visit to see its workflow and history.</div> : detailLoading ? <WorkbenchLoading/> : detail ? <SiteVisitDetail
            data={detail}
            filters={filters}
            assignment={assignment}
            setAssignment={setAssignment}
            statusNote={statusNote}
            setStatusNote={setStatusNote}
            saving={saving}
            onBack={() => selectVisit("")}
            onAssign={() => mutate(`/v1/admin/site-visits-workbench/${selectedVisitId}/assign`, { ...assignment, visit_at: assignment.visit_at ? new Date(assignment.visit_at).toISOString() : null }, "Site Visit schedule updated")}
            onStatus={status => mutate(`/v1/admin/site-visits-workbench/${selectedVisitId}/transition`, { status, note: statusNote }, `Site Visit marked ${labelize(status)}`)}
          /> : <WorkbenchEmpty title="Site Visit unavailable" detail="Return to the list and select another visit."/>}
        </aside>
      </div>
    </section>}
  </div>;
}

function PropertyVisitGroup({ property, expanded, selectedVisitId, visits, visitTotal, visitPage, visitLoading, visitError, onToggle, onSelect, onVisitPage }) {
  const panelId = `property-visits-${property.property_id}`;
  return <article className={`overflow-hidden rounded-2xl border bg-background transition-shadow duration-200 ease-out ${expanded ? "border-primary/35 shadow-md" : "border-border hover:shadow-sm"}`}>
    <div className="grid gap-4 p-4 lg:grid-cols-[minmax(240px,1.3fr)_repeat(2,minmax(130px,.55fr))_auto] lg:items-center">
      <div className="flex min-w-0 gap-3"><PropertyThumb src={property.thumbnail_url} alt={property.property_title}/><div className="min-w-0"><EntityLink to={propertyLink(property.property_id)} className="block truncate">{property.property_title || "Untitled property"}</EntityLink><p className="mt-1 truncate text-xs text-muted-foreground">{property.property_code || "No code"} · {labelize(property.property_type)}</p><p className="mt-1 truncate text-xs text-muted-foreground">{[property.locality, property.city].filter(Boolean).join(", ") || "Location unavailable"}</p></div></div>
      <div className="grid grid-cols-2 gap-2 text-xs"><Metric label="Visits" value={property.visit_count}/><Metric label="Buyers" value={property.buyer_count}/><Metric label="Agents" value={property.agent_count}/><Metric label="Offices" value={property.office_count}/></div>
      <div className="grid grid-cols-2 gap-2 text-xs"><Metric label="Upcoming" value={property.upcoming_count}/><Metric label="Completed" value={property.completed_count}/><Metric label="Unassigned" value={property.unassigned_count} warning={Number(property.unassigned_count) > 0}/><Metric label="Overdue" value={property.overdue_count} danger={Number(property.overdue_count) > 0}/></div>
      <div className="flex flex-col gap-2 lg:items-end"><p className="text-xs text-muted-foreground">Next: <span className="font-semibold text-foreground">{formatDateTime(property.next_visit_at)}</span></p><Button type="button" size="sm" variant={expanded ? "secondary" : "outline"} onClick={onToggle} aria-expanded={expanded} aria-controls={panelId} title={expanded ? "Hide buyer Site Visits" : "Show all buyer Site Visits for this property"}>{expanded ? <ChevronUp/> : <ChevronDown/>}{expanded ? "Hide visits" : "View visits"}</Button></div>
    </div>
    {expanded && <div id={panelId} className="border-t bg-muted/15">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3"><div><p className="font-bold">Buyer Site Visits</p><p className="text-xs text-muted-foreground">{count(visitTotal)} matching visits under this property</p></div><Badge variant="outline">Page {visitPage}</Badge></div>
      {visitError ? <div className="p-4"><WorkbenchError message={visitError}/></div> : visitLoading ? <WorkbenchLoading/> : visits.length === 0 ? <WorkbenchEmpty title="No matching visits"/> : <div className="divide-y">{visits.map(visit => <VisitRow key={visit.site_visit_id} visit={visit} selected={selectedVisitId === visit.site_visit_id} onSelect={() => onSelect(visit.site_visit_id)}/>)}</div>}
      {!visitLoading && Number(visitTotal) > VISIT_PAGE_SIZE && (
        <WorkbenchPagination page={visitPage} limit={VISIT_PAGE_SIZE} total={visitTotal} onPage={onVisitPage}/>
      )}
    </div>}
  </article>;
}

function Metric({ label, value, warning = false, danger = false }) {
  const tone = danger ? "text-destructive" : warning ? "text-amber-700 dark:text-amber-300" : "text-foreground";
  return <span className="rounded-lg border bg-card p-2 text-muted-foreground">{label}<b className={`mt-0.5 block text-base tabular-nums ${tone}`}>{count(value)}</b></span>;
}

function VisitRow({ visit, selected, onSelect }) {
  const schedulerIsRequester = String(visit.scheduled_by_role || "").toUpperCase() === "BUYER";
  return <div className={`grid gap-3 p-4 transition-colors duration-200 ease-out hover:bg-muted/40 md:grid-cols-2 lg:grid-cols-[minmax(160px,.8fr)_minmax(165px,.8fr)_minmax(170px,.8fr)_minmax(150px,.7fr)_auto] ${selected ? "bg-primary/5 ring-1 ring-inset ring-primary/25" : ""}`}>
    <div><p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Buyer</p><EntityLink to={visit.buyer_user_id && userLink(visit.buyer_user_id)}>{visit.buyer_name || "Unknown buyer"}</EntityLink><p className="mt-1 text-xs"><Phone className="mr-1 inline h-3 w-3"/>{visit.buyer_phone ? <a href={`tel:${visit.buyer_phone}`} className="hover:underline" title={`Call ${visit.buyer_name || "buyer"}`}>{visit.buyer_phone}</a> : "No phone"}</p></div>
    <div><p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Assigned team</p><EntityLink to={visit.agent_id && userLink(visit.agent_id)}>{visit.agent_name || "Unassigned"}</EntityLink><p className="mt-1 text-xs"><EntityLink to={visit.office_id && officeLink(visit.office_id)}>{visit.office_name || "No office"}</EntityLink></p></div>
    <div><p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{schedulerIsRequester ? "Requested by" : "Scheduled by"}</p><EntityLink to={visit.scheduled_by_user_id && userLink(visit.scheduled_by_user_id)}>{visit.scheduled_by_name || "Unknown"}</EntityLink><p className="mt-1 text-xs text-muted-foreground">{labelize(visit.scheduled_by_role)} · {formatDateTime(visit.scheduled_by_at)}</p></div>
    <div><p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Schedule & status</p><p className="font-semibold">{formatDateTime(visit.visit_at)}</p><div className="mt-1 flex flex-wrap gap-1"><StatusPill value={visit.status}/>{visit.overdue && <StatusPill value="OVERDUE"/>}</div></div>
    <div className="flex items-center justify-end"><Button type="button" size="sm" variant="ghost" onClick={onSelect} title={`Open Site Visit details for ${visit.buyer_name || "buyer"}`}>Details<ChevronRight/></Button></div>
  </div>;
}

// Shared by the full Workbench and the focused Assignment & Management queue.
export function SiteVisitDetail({ data, filters, assignment, setAssignment, statusNote, setStatusNote, saving, onBack, onAssign, onStatus }) {
  const visit = data.visit || {};
  const events = Array.isArray(data.events) ? data.events : [];
  const allAssignableAgents = filters.assignable_agents || filters.agents || [];
  const assignableAgents = assignment.office_id
    ? allAssignableAgents.filter(agent => String(agent.office_id || "") === String(assignment.office_id))
    : allAssignableAgents;
  const normalizedStatus = String(visit.status || "").toUpperCase();
  const terminal = ["COMPLETED", "CANCELLED", "NO_SHOW"].includes(normalizedStatus);
  const visitAt = visit.visit_at ? new Date(visit.visit_at) : null;
  const pendingLike = ["PENDING", "REQUESTED", "OPEN"].includes(normalizedStatus);
  const canMarkNoShow = Boolean(visit.agent_id && visitAt && !Number.isNaN(visitAt.getTime()) && visitAt <= new Date());
  // Mirror the backend transition rules so the UI does not offer actions that
  // will be rejected or allow a scheduled visit to regress to pending.
  const statusOptions = normalizedStatus === "IN_PROGRESS"
    ? ["CANCELLED"]
    : [...(pendingLike ? ["PENDING"] : []), "SCHEDULED", "CANCELLED", ...(canMarkNoShow ? ["NO_SHOW"] : [])];
  const earliestSchedule = toDateTimeLocalValue(new Date(Date.now() + 5 * 60 * 1000));
  const latestSchedule = toDateTimeLocalValue(new Date(Date.now() + 730 * 24 * 60 * 60 * 1000));
  const schedulerIsRequester = String(visit.scheduled_by_role || "").toUpperCase() === "BUYER";

  return <div className="space-y-5 p-4 sm:p-5"><MobileBack onClick={onBack} className="xl:hidden"/>
    <div><p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Site Visit</p><div className="mt-1 flex flex-wrap items-center gap-2"><h2 className="font-display text-xl font-black">{visit.property_title || "Unlinked property"}</h2><StatusPill value={visit.status}/></div><p className="mt-1 text-sm text-muted-foreground"><CalendarClock className="mr-1 inline h-4 w-4"/>{formatDateTime(visit.visit_at)}</p></div>
    <div className="rounded-xl border bg-background p-4 text-sm"><p className="font-bold">Relationships</p><div className="mt-3 space-y-2">
      <p><span className="text-muted-foreground">Property: </span><EntityLink to={visit.property_id && propertyLink(visit.property_id)}>{visit.property_title}</EntityLink></p>
      <p><span className="text-muted-foreground">Buyer: </span><EntityLink to={visit.buyer_user_id && userLink(visit.buyer_user_id)}>{visit.buyer_name}</EntityLink></p>
      <p><Phone className="mr-2 inline h-4 w-4"/>{visit.buyer_phone ? <a href={`tel:${visit.buyer_phone}`} className="hover:underline" title="Call buyer">{visit.buyer_phone}</a> : "No phone"}</p>
      <p><Mail className="mr-2 inline h-4 w-4"/>{visit.buyer_email ? <a href={`mailto:${visit.buyer_email}`} className="hover:underline" title="Email buyer">{visit.buyer_email}</a> : "No email"}</p>
      <p><span className="text-muted-foreground">Agent: </span><EntityLink to={visit.agent_id && userLink(visit.agent_id)}>{visit.agent_name || "Unassigned"}</EntityLink></p>
      <p><span className="text-muted-foreground">Office: </span><EntityLink to={visit.office_id && officeLink(visit.office_id)}>{visit.office_name || "No office"}</EntityLink></p>
      <p><span className="text-muted-foreground">Lead: </span><EntityLink to={visit.lead_id && leadLink(visit.lead_id)}>{visit.lead_id ? "Open CRM lead" : "No linked lead"}</EntityLink></p>
    </div></div>
    <div className="rounded-xl border bg-background p-4 text-sm"><p className="font-bold">Workflow ownership</p><div className="mt-3 space-y-2"><p><span className="text-muted-foreground">{schedulerIsRequester ? "Requested by: " : "Scheduled by: "}</span><EntityLink to={visit.scheduled_by_user_id && userLink(visit.scheduled_by_user_id)}>{visit.scheduled_by_name || "Unknown"}</EntityLink></p><p><span className="text-muted-foreground">Actor role: </span>{labelize(visit.scheduled_by_role)}</p><p><Clock3 className="mr-2 inline h-4 w-4"/>{formatDateTime(visit.scheduled_by_at)}</p></div></div>
    {!terminal && <div className="rounded-xl border bg-background p-4"><p className="font-bold"><UserRoundCheck className="mr-2 inline h-4 w-4"/>Assign or reschedule</p><div className="mt-3 grid gap-3"><FilterField label="Office (optional)" value={assignment.office_id} onChange={value => setAssignment(current => ({ ...current, office_id: value, agent_id: "" }))} choices={options(filters.offices, "Derive from agent")}/><FilterField label="Agent" value={assignment.agent_id} onChange={value => setAssignment(current => ({ ...current, agent_id: value }))} choices={options(assignableAgents, "Select active verified agent")}/><FilterField label="Visit date and time" type="datetime-local" min={earliestSchedule} max={latestSchedule} value={assignment.visit_at} onChange={value => setAssignment(current => ({ ...current, visit_at: value }))}/><Textarea value={assignment.note} onChange={event => setAssignment(current => ({ ...current, note: event.target.value }))} placeholder="Reason or scheduling note (optional)" maxLength={500}/><Button disabled={saving || !assignment.agent_id || !assignment.visit_at} onClick={onAssign} title="Assign the agent and save this Site Visit schedule">{saving ? "Saving…" : "Save schedule"}</Button></div></div>}
    {!terminal && <div className="rounded-xl border bg-background p-4"><p className="font-bold">Admin status controls</p><p className="mt-1 text-xs text-muted-foreground">Start and completion remain protected by the existing agent/buyer verification flow. No-show becomes available only after the scheduled time.</p><Textarea className="mt-3" value={statusNote} onChange={event => setStatusNote(event.target.value)} placeholder="Reason required for cancellation or no-show" maxLength={500}/><div className="mt-3 flex flex-wrap gap-2">{statusOptions.map(status => <Button key={status} size="sm" variant={status === normalizedStatus ? "default" : "outline"} disabled={saving || status === normalizedStatus || (status === "SCHEDULED" && (!visit.agent_id || !visit.visit_at)) || (["CANCELLED", "NO_SHOW"].includes(status) && !statusNote.trim())} onClick={() => onStatus(status)} title={`Mark this Site Visit ${labelize(status)}`}>{labelize(status)}</Button>)}</div></div>}
    <div><p className="font-bold">History</p><div className="mt-3 space-y-3">{events.length === 0 ? <p className="text-sm text-muted-foreground">No workflow history recorded yet.</p> : events.map(event => <div key={event.id} className="rounded-xl border bg-background p-3 text-sm"><div className="flex items-start justify-between gap-2"><p className="font-bold">{labelize(event.event_type)}</p><span className="text-xs text-muted-foreground">{formatDateTime(event.created_at)}</span></div>{(event.previous_status || event.new_status) && <p className="mt-1 text-muted-foreground">{labelize(event.previous_status)} → {labelize(event.new_status)}</p>}<p className="mt-1">{cleanNoteText(event.note, event.actor_name)}</p><p className="mt-1 text-xs text-muted-foreground">By {event.actor_name || "System"}</p></div>)}</div></div>
  </div>;
}
